import { motion } from "framer-motion";
import { Users, Truck, BadgeEuro, Wind } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const Card = ({ className = "", delay = 0, children, testid }) => (
  <Reveal delay={delay} className={className}>
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      data-testid={testid}
      className="group relative h-full overflow-hidden rounded-[26px]"
    >
      {children}
    </motion.article>
  </Reveal>
);

const PhotoCard = ({ img, alt, icon: I, title, text, tag }) => (
  <div className="relative flex h-full min-h-[340px] flex-col justify-end bg-[var(--navy)] p-7">
    <img src={img} alt={alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1D33] via-[#0A1D33]/70 to-transparent" />
    <div className="relative">
      <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white backdrop-blur">
        <I className="h-4 w-4" /> {tag}
      </span>
      <h3 className="font-display text-2xl font-extrabold leading-tight text-white sm:text-[1.7rem]">{title}</h3>
      <p className="mt-2 max-w-md text-[15px] leading-relaxed text-white/85">{text}</p>
    </div>
  </div>
);

export const Guarantees = () => (
  <section id="garanciak" data-testid="guarantees-section" className="py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Garanciáink</Eyebrow>
          <h2 className="font-display mt-5 text-3xl font-black leading-[1.05] tracking-tight text-[var(--navy)] sm:text-5xl">
            Miért a Magyar <br className="hidden sm:block" />Extrém Takarítás?
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-base leading-relaxed text-[var(--muted)] sm:text-lg">
            Egyenes beszéd, írásban rögzített fix ár és egy csapat, amely tudja, hogy ezek a helyzetek mennyire érzékenyek.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid auto-rows-[minmax(340px,auto)] grid-cols-1 gap-5 md:grid-cols-6">
        <Card className="md:col-span-4" testid="guarantee-card-hazai">
          <PhotoCard img="/img/clean_interior.webp" alt="Tisztára fertőtlenített, világos lakás" icon={Users} tag="100% hazai" title="100% Hazai, megbízható szakembercsapat" text="Magyar szakemberek, magyar nyelvű, közvetlen kommunikáció az első hívástól az átadásig." />
        </Card>
        <Card className="md:col-span-2" delay={0.08} testid="guarantee-card-diszkrecio">
          <PhotoCard img="/img/van_discreet.webp" alt="Jelzés és felirat nélküli sötétkék furgon" icon={Truck} tag="Diszkréció" title="Garantált diszkréció" text="Jelölés és felirat nélküli gépjárművek – a szomszédok semmit sem látnak." />
        </Card>
        <Card className="md:col-span-3" delay={0.04} testid="guarantee-card-fixar">
          <div className="relative flex h-full flex-col justify-between overflow-hidden bg-[var(--crimson)] p-7 text-white">
            <span className="font-display pointer-events-none absolute -right-6 -top-10 select-none text-[11rem] font-black leading-none text-white/10">100%</span>
            <span className="relative inline-flex w-fit items-center gap-2 rounded-full border border-white/30 px-3 py-1 text-xs font-bold uppercase tracking-wider">
              <BadgeEuro className="h-4 w-4" /> Fix ár garancia
            </span>
            <div className="relative">
              <h3 className="font-display text-2xl font-extrabold leading-tight sm:text-[1.9rem]">Átlátható, fix áras ajánlatok</h3>
              <p className="mt-2 max-w-md text-[15px] leading-relaxed text-white/90">Rejtett költségek nélkül. Amit az ajánlatban leírunk, annyit fizet – utólagos meglepetések nélkül.</p>
            </div>
          </div>
        </Card>
        <Card className="md:col-span-3" delay={0.1} testid="guarantee-card-fertotlenites">
          <div className="grid h-full grid-cols-1 bg-white sm:grid-cols-2">
            <div className="flex flex-col justify-between gap-6 p-7">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[var(--navy)]/8 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--navy)] ring-1 ring-[var(--navy)]/15">
                <Wind className="h-4 w-4" /> Minősített eljárás
              </span>
              <div>
                <h3 className="font-display text-2xl font-extrabold leading-tight text-[var(--navy)]">Biológiai fertőtlenítés & ózonos szagtalanítás</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">Professzionális gépekkel, a szagok forrásáig hatolva.</p>
              </div>
            </div>
            <div className="relative min-h-[220px] overflow-hidden">
              <img src="/img/ozone_machine.webp" alt="Ózongenerátor és légtisztító egy üres szobában" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  </section>
);
